/**
 * Deterministic evaluator for the calibrated national custom-tour model.
 *
 * This module deliberately contains no networking or UI policy. The fitted
 * SVR parameters are checked in by `tours-package/scripts/train-custom-quote-model.py`,
 * so a quote can be inspected and reproduced without bundling scikit-learn in
 * either the mobile app or an Edge Function.
 *
 * Keep this file byte-for-byte aligned with
 * `supabase/functions/_shared/tours-custom-quote-model.ts`. The parity gate
 * treats drift as a release blocker: the device may preview a price, but only
 * the server is allowed to create the private experience and take payment.
 */
import { CUSTOM_TOUR_QUOTE_MODEL_ARTIFACT } from './custom-quote-model.generated'

export const CUSTOM_TOUR_QUOTE_MAX_ROUTE_KM = 300
export const CUSTOM_TOUR_QUOTE_MIN_POINTS = 2
export const CUSTOM_TOUR_QUOTE_MAX_POINTS = 4

export type CustomTourQuoteVehicleType =
  (typeof CUSTOM_TOUR_QUOTE_MODEL_ARTIFACT.training.scope.vehicleTypes)[number]

export interface CustomTourQuoteModelInput {
  vehicleType: CustomTourQuoteVehicleType
  vehicleCapacity: number
  /** Origin + optional intermediate stops + destination. */
  stopCount: number
  /** Server-authoritative road distance, in kilometers. */
  distanceKm: number
}

export interface CustomTourQuoteModelResult {
  modelVersion: string
  rawPredictionUsd: number
  /** Calibrated vehicle total before platform payment economics. */
  safePriceCents: number
  isWithinTrainingDomain: boolean
}

function finite(value: number, name: string): void {
  if (!Number.isFinite(value)) throw new Error(`${name} must be finite`)
}

function positiveInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${name} must be a positive integer`)
  }
}

function at<T>(values: readonly T[], index: number, name: string): T {
  const value = values[index]
  if (value === undefined) throw new Error(`Model artifact is missing ${name}[${index}]`)
  return value
}

function dot(left: readonly number[], right: readonly number[]): number {
  if (left.length !== right.length) throw new Error('Model vectors have incompatible dimensions')
  let total = 0
  for (let index = 0; index < left.length; index += 1) {
    total += at(left, index, 'features') * at(right, index, 'supportVector')
  }
  return total
}

export function isCustomTourQuoteVehicleType(value: unknown): value is CustomTourQuoteVehicleType {
  return (
    typeof value === 'string' &&
    (CUSTOM_TOUR_QUOTE_MODEL_ARTIFACT.training.scope.vehicleTypes as readonly string[]).includes(
      value,
    )
  )
}

export function customTourQuoteIsWithinTrainingDomain(input: CustomTourQuoteModelInput): boolean {
  const domain = CUSTOM_TOUR_QUOTE_MODEL_ARTIFACT.training.domain
  return (
    input.stopCount >= domain.stopCount.min &&
    input.stopCount <= domain.stopCount.max &&
    input.distanceKm >= domain.distanceKm.min &&
    input.distanceKm <= domain.distanceKm.max
  )
}

/**
 * Evaluate the exact degree-two SVR exported from scikit-learn.
 *
 * Direct origin → destination trips have two points while the observed catalog
 * only contains routes with three or four. We preserve the factual input
 * rather than silently padding a waypoint; callers receive the domain bit for
 * audit/observability, and the owner's safety calibration is applied to every
 * output. The server still bounds all national routes to a conservative 300km.
 */
export function scoreCustomTourQuoteModel(
  input: CustomTourQuoteModelInput,
): CustomTourQuoteModelResult {
  if (!isCustomTourQuoteVehicleType(input.vehicleType)) {
    throw new Error('Unsupported custom-tour vehicle type')
  }
  positiveInteger(input.vehicleCapacity, 'vehicleCapacity')
  if (
    !Number.isSafeInteger(input.stopCount) ||
    input.stopCount < CUSTOM_TOUR_QUOTE_MIN_POINTS ||
    input.stopCount > CUSTOM_TOUR_QUOTE_MAX_POINTS
  ) {
    throw new Error(
      `stopCount must be between ${CUSTOM_TOUR_QUOTE_MIN_POINTS} and ${CUSTOM_TOUR_QUOTE_MAX_POINTS}`,
    )
  }
  finite(input.distanceKm, 'distanceKm')
  if (input.distanceKm <= 0 || input.distanceKm > CUSTOM_TOUR_QUOTE_MAX_ROUTE_KM) {
    throw new Error(
      `distanceKm must be greater than zero and at most ${CUSTOM_TOUR_QUOTE_MAX_ROUTE_KM}`,
    )
  }

  const artifact = CUSTOM_TOUR_QUOTE_MODEL_ARTIFACT
  const scaler = artifact.training.scaler
  const continuous = [input.vehicleCapacity, input.stopCount, input.distanceKm]
  const features = continuous.map(
    (value, index) =>
      (value - at(scaler.mean, index, 'scaler.mean')) / at(scaler.scale, index, 'scaler.scale'),
  )
  for (const vehicleType of artifact.training.scope.vehicleTypes) {
    features.push(vehicleType === input.vehicleType ? 1 : 0)
  }

  let rawPredictionUsd = artifact.model.intercept
  for (let index = 0; index < artifact.model.supportVectors.length; index += 1) {
    const coefficient = at(artifact.model.dualCoefficients, index, 'dualCoefficients')
    const supportVector = at(artifact.model.supportVectors, index, 'supportVectors')
    const kernel = Math.pow(
      artifact.model.gamma * dot(features, supportVector) + artifact.model.coef0,
      artifact.model.degree,
    )
    rawPredictionUsd += coefficient * kernel
  }

  if (!Number.isFinite(rawPredictionUsd) || rawPredictionUsd <= 0) {
    throw new Error('Custom-tour model emitted an invalid price')
  }
  const safePriceCents = Math.ceil(
    rawPredictionUsd * (1 + artifact.calibration.upliftRate) * 100 - 1e-9,
  )
  if (!Number.isSafeInteger(safePriceCents) || safePriceCents < 1) {
    throw new Error('Custom-tour model emitted an invalid calibrated price')
  }

  return {
    modelVersion: artifact.modelVersion,
    rawPredictionUsd,
    safePriceCents,
    isWithinTrainingDomain: customTourQuoteIsWithinTrainingDomain(input),
  }
}
